<template>
    <v-form
        v-if="form"
        ref="form"
        class="client-form"
        max-width="750px"
    >
        <v-row>
            <v-col class="pt-3 pb-0" cols="12" sm="6">
                <v-text-field
                    v-model="form.name"
                    :error-messages="form.$errors.name"
                    :label="$t('general.name')"
                    @blur="form.validate('name')"
                    @input="delete form.$errors.name"
                />
            </v-col>

            <v-col class="pt-3 pb-0" cols="12" sm="6">
                <v-text-field
                    v-model="form.cost_center"
                    v-mask="locale === 'sr' ? '##:?N?N?N?N?N?N?N?N?N?N?N?N?N?N' : ''"
                    :error-messages="form.$errors.cost_center"
                    :label="$t('general.buyer_cost_center')"
                    @blur="form.validate('cost_center')"
                    @input="delete form.$errors.cost_center"
                />
            </v-col>
        </v-row>

        <v-row>
            <v-col class="pt-3 pb-0" cols="12" sm="6">
                <v-text-field
                    v-model="form.phone"
                    :error-messages="form.$errors.phone"
                    :label="$t('general.phone')"
                    @blur="form.validate('phone')"
                    @input="delete form.$errors.phone"
                />
            </v-col>

            <v-col class="pt-3 pb-0" cols="12" sm="6">
                <v-text-field
                    v-model="form.email"
                    :error-messages="form.$errors.email"
                    :label="$t('general.email')"
                    @blur="form.validate('email')"
                    @input="delete form.$errors.email"
                />
            </v-col>
        </v-row>

        <v-row>
            <v-col class="pt-3 pb-0" cols="12" sm="6">
                <v-text-field
                    v-model="form.address"
                    :error-messages="form.$errors.address"
                    :label="$t('general.address')"
                    @blur="form.validate('address')"
                    @input="delete form.$errors.address"
                />
            </v-col>

            <v-col class="pt-3 pb-0" cols="12" sm="6">
                <v-text-field
                    v-model="form.postal_number"
                    :error-messages="form.$errors.postal_number"
                    :label="$t('general.postal_number')"
                    @blur="form.validate('postal_number')"
                    @input="delete form.$errors.postal_number"
                />
            </v-col>
        </v-row>

        <v-row>
            <v-col class="pt-3 pb-0" cols="12" sm="6">
                <v-text-field
                    v-model="form.city"
                    :error-messages="form.$errors.city"
                    :label="$t('general.city')"
                    @blur="form.validate('city')"
                    @input="delete form.$errors.city"
                />
            </v-col>

            <v-col class="pt-3 pb-0" cols="12" sm="6">
                <v-autocomplete
                    v-model="form.country"
                    :items="countries"
                    :label="$t('general.country')"
                    :placeholder="$t('general.country')"
                    :error-messages="form.$errors.country"
                    item-text="name"
                    item-value="alpha3Code"
                    clearable
                    auto-select-first
                    clear-icon="fal fa-times"
                    @change="onCountryChange($event)"
                />
            </v-col>
        </v-row>

        <v-row>
            <v-col class="pt-3 pb-0" cols="12" sm="6">
                <v-select
                    v-model="form.id_type"
                    :error-messages="form.$errors.id_type"
                    :items="idTypes"
                    :label="$t('general.id_type')"
                    item-value="id"
                    item-text="text"
                    :disabled="locale === 'me' && form.country === 'MNE'"
                    @blur="form.validate('id_type')"
                    @input="delete form.$errors.id_type"
                />
            </v-col>

            <v-col class="pt-3 pb-0" cols="12" sm="6">
                <v-text-field
                    v-model="form.id_number"
                    v-mask="idNumberMask"
                    :error-messages="form.$errors.id_number"
                    :loading="loadingIdNumber"
                    :disabled="isDisabled"
                    :label="$t('general.id_number')"
                    @blur="form.validate('id_number')"
                    @input="delete form.$errors.id_number"
                />
            </v-col>

            <v-col class="pt-3 pb-0" cols="12" sm="6">
                <v-text-field
                    v-model="form.registration_number"
                    :error-messages="form.$errors.registration_number"
                    :label="$t('general.registration_number')"
                    @blur="form.validate('registration_number')"
                    @input="delete form.$errors.registration_number"
                />
            </v-col>

            <v-col class="pt-3 pb-0" cols="12" sm="6">
                <v-text-field
                    v-model="form.vat_number"
                    :error-messages="form.$errors.vat_number"
                    :label="$t('general.vat_number')"
                    @blur="form.validate('vat_number')"
                    @input="delete form.$errors.vat_number"
                />
            </v-col>
        </v-row>

        <v-row>
            <v-col
                v-if="enablePricelists"
                class="pt-3 pb-0"
                cols="12"
                sm="6"
            >
                <v-select
                    v-model="form.pricelist_id"
                    :items="pricelists"
                    item-value="id"
                    item-text="name"
                    clearable
                    clear-icon="fal fa-times"
                    :label="$tc('general.pricelist')"
                />
            </v-col>

            <v-col
                class="pt-3 pb-0"
                cols="12"
                :sm="enablePricelists ? 6 : 12"
            >
                <number-field
                    v-model="form.discount_percent"
                    :error-messages="form.$errors.discount_percent"
                    :label="$t('general.discount')"
                    :precision="2"
                    :max="99"
                    suffix="%"
                    unsigned
                    @blur="form.validate('discount_percent')"
                    @input="delete form.$errors.discount_percent"
                />
            </v-col>
        </v-row>

        <v-row>
            <v-col
                class="pt-3 pb-0"
                cols="12"
                :sm="locale === 'me' ? 6 : 12"
            >
                <v-text-field
                    v-model="form.card"
                    :error-messages="form.$errors.card"
                    :label="$t('general.buyer_card_number')"
                    @blur="form.validate('card')"
                    @input="delete form.$errors.card"
                />
            </v-col>

            <v-col
                v-if="locale === 'me'"
                class="pt-3 pb-0"
                cols="12"
                sm="6"
            >
                <v-text-field
                    v-model="form.tax_id_card_number"
                    :error-messages="form.$errors.tax_id_card_number"
                    :label="$t('general.tax_id_card_number')"
                    :hint="$t('general.tax_id_card_number_description')"
                    persistent-hint
                    @blur="form.validate('tax_id_card_number')"
                    @input="delete form.$errors.tax_id_card_number"
                />
            </v-col>
        </v-row>

        <v-textarea
            v-model="form.comment"
            class="mt-7"
            :error-messages="form.$errors.comment"
            :label="$t('general.comment')"
            :rows="1"
            @blur="form.validate('comment')"
            @input="delete form.$errors.comment"
        />

        <div class="d-flex justify-center mb-3">
            <v-btn
                color="primary"
                outlined
                @click="save"
            >
                {{ $t('general.save') }}
            </v-btn>
        </div>

    </v-form>
</template>

<script>
import Client from '@/models/Client'
import Pricelist from '@/models/Pricelist';
import ApiService from '@/services/ApiService'
import Countries from '@/utilities/Countries';
import NumberField from '@/components/NumberField';
import * as rules from 'vuelidate/lib/validators'

export default {
    components: {
        NumberField
    },

    props: {
        client: {
            type: Object,
            default: () => null
        },
        admin: {
            type: Boolean,
            default: false
        },
        pricelists: {
            type: Array,
            default: () => Pricelist.all()
        },
    },

    created() {
        this.countries = Countries.data;
        this.form = this.createForm(this.client);
    },

    data() {
        return {
            countries: [],
            form: null,
            loadingIdNumber: false,
        };
    },

    validations: {
        form: {
            name: { requiredIf: rules.requiredIf(form => locale !== 'sr' || !form.id_type?.includes('tax_id_number')) },
            cost_center: {
                costCenter: rules.helpers.withParams(
                    { type: 'costCenter' },
                    function(value) {
                        if(!value) return true
                        if(locale !== 'sr') return true

                        const segments = value.split(':');
                        if(segments.length < 2) return false;

                        const idTypePrefixes = Client.idTypes().map(type => type.prefix);
                        if(!idTypePrefixes.includes(segments[0])) return false;

                        return true
                    }
                )
            },
            email: { email: rules.email },
            address: { requiredIf: rules.requiredIf(form => locale === 'ba' || (locale === 'me' && form.country !== 'MNE')) },
            city: { requiredIf: rules.requiredIf(form => locale === 'ba' || (locale === 'me' && form.country !== 'MNE')) },
            postal_number: { requiredIf: rules.requiredIf(form => locale === 'ba') },
            country: { required: rules.required },
            id_type: { required: rules.required },
            id_number: {
                required: rules.required,
                taxIdNumber: rules.helpers.withParams(
                    { type: 'taxIdNumber' },
                    function(value) {
                        if(!value) return true

                        if(locale === 'sr') {
                            if(this.form.country === 'SRB' && this.form.id_type === 'tax_id_number_local') return value.length === 9
                            if(this.form.country === 'SRB' && this.form.id_type === 'tax_id_number_local_jbkjs') return /^\d{9}:\d{5}$/.test(value);
                            if(this.form.country === 'SRB' && this.form.id_type === 'personal_id_number_local') return value.length === 13
                        } else if(locale === 'me') {
                            if(this.form.country === 'MNE' && this.form.id_type === 'tax_id_number') return value.length === 8 || value.length === 13
                        } else if(locale === 'ba') {
                            if(this.form.country === 'BIH' && this.form.id_type === 'tax_id_number') return value.length === 13
                        }

                        return true
                    }
                )
            },
            discount_percent: {
                required: rules.required,
                between: rules.between(0, 99)
            },
        }
    },

    watch: {
        client(val) {
            this.form = this.createForm(val)
        },

        'form.id_type'(val) {
            this.form.validate()
        },

        'form.id_number'(val) {
            if (locale === 'sr' && val && val.length === 9 && this.form.id_type === 'tax_id_number_local') {
                this.searchTaxIdNumber(val, 'SRB');
            }

            if (locale === 'me' && val && val.length === 8 && this.form.id_type === 'tax_id_number') {
                this.searchTaxIdNumber(val, 'ME');
            }
        },

    },

    computed: {
        idTypes() {
            return Client.idTypes().map(idType => {
                if(locale === 'me' && this.form?.country === 'MNE' && idType.id === 'tax_id_number') {
                    idType.text += ' / ' + this.$t('clients.id_types.personal_id_number');
                }

                return idType;
            }).filter(idType => {
                if(locale === 'me' && this.form?.country && this.form.country !== 'MNE' && idType.id === 'tax_id_number') {
                    return false;
                }

                return true;
            })
        },

        idNumberMask() {
            let mask = 'XXXXXXXXXXXXXXXXXXXX'

            if(locale === 'sr') {
                if(this.form.country === 'SRB' && this.form.id_type === 'tax_id_number_local') return '#########'
                if(this.form.country === 'SRB' && this.form.id_type === 'tax_id_number_local_jbkjs') return '#########:#####'
                if(this.form.country === 'SRB' && this.form.id_type === 'personal_id_number_local') return '#############'
            } else if(locale === 'me') {
                if(this.form.country === 'MNE' && this.form.id_type === 'tax_id_number') return '#############'
            } else if(locale === 'ba') {
                if(this.form.country === 'BIH' && this.form.id_type === 'tax_id_number') return '#############'
            }

            return mask
        },

        enablePricelists() {
            return !this.tenant.venue?.settings?.use_inventory_accounting && !isAllegra;
        },

        isDisabled() {
            return this.form.busy || this.loadingIdNumber;
        }
    },

    methods: {
        createForm(client = null) {
            let country = client ? client.country : null
            if(!country) {
                if(locale === 'sr') country = 'SRB'
                else if(locale === 'me') country = 'MNE'
                else if(locale === 'ba') country = 'BIH'
            }

            const form = new Form(this, 'form', {
                id: client ? client.id : null,
                name: client ? client.name : null,
                cost_center: client ? client.cost_center : null,
                address: client ? client.address : null,
                postal_number: client ? client.postal_number : null,
                city: client ? client.city : null,
                country: country,
                phone: client ? client.phone : null,
                email: client ? client.email : null,
                tax_id_number: client ? client.tax_id_number : null,
                registration_number: client ? client.registration_number : null,
                vat_number: client ? client.vat_number : null,
                card: client ? client.card : null,
                discount_percent: client ? parseFloat(client.discount_percent) : 0,
                comment: client ? client.comment : null,
                id_type: client ? client.id_type : this.idTypes[0].id,
                id_number: client ? client.id_number : null,
                tax_id_card_number: client ? client.tax_id_card_number : null,
                pricelist_id: client ? client.pricelist_id : null,
            })

            return form
        },

        /**
         * Save Client
         */
        save() {
            if (!this.form.validate()) return;

            if(!this.form.name) {
                this.form.name = this.$tc('general.buyer', 1)

                const localCountry = (locale === 'me' && this.form.country === 'MNE')
                    || (locale === 'sr' && this.form.country === 'SRB')
                    || (locale === 'ba' && this.form.country === 'BIH')

                if(!localCountry) this.form.name += ' ' + this.form.country

                if(this.form.id_type?.includes('tax_id_number')) {
                    this.form.name += ` (${this.$t('clients.id_types.tax_id_number')}: ${this.form.id_number})`
                }
            }

            if(!this.isOnline) {
                this.$snackbar(this.$t('connection.internet_offline_message_not_allowed'), 'error');
                return
            }

            const client = new Client(this.form.toJson());
            const taxIdNumber = client.getTaxIdNumber();
            if(taxIdNumber) {
                this.form.tax_id_number = taxIdNumber;
            }

            const url = this.admin ? '/admin/clients' : '/clients'

            if (!this.form.id) {
                ApiService.post(url, this.form).then(response => {
                    this.$emit('save', new Client(response.data))
                    this.form = this.createForm()

                    Client.insert({ data: [response.data] });
                    this.$snackbar(this.$t('general.client_saved'), 'success');
                });
            } else {
                ApiService.patch(`${url}/${this.form.id}`, this.form).then(response => {
                    this.$emit('save', new Client(response.data))

                    Client.insert({ data: [response.data] });
                    this.$snackbar(this.$t('clients.updated'), 'success');
                });
            }
        },

        onCountryChange(country) {
            this.form.id_type = country === 'MNE' && locale === 'me' ? this.idTypes[0].id : null
            this.form.id_number = null
        },

        searchTaxIdNumber(val, countryCode) {
            this.loadingIdNumber = true;

            ApiService.get(`/clients/search/${val}`, {
                params: { countryCode: countryCode }
            }).then((response) => {
                if (response.data.success) {
                    this.form.name = response.data.company.short_name;
                    this.form.address = response.data.company.address;
                    this.form.city = response.data.company.city;
                    this.form.registration_number = response.data.company.registration_number;
                }
            })
            .finally(() => {
                this.loadingIdNumber = false;
            });
        }
    }
};
</script>

<style lang="scss" scoped>
.client-form {
    padding: 10px 20px;
}
</style>
